import { type PaletteMode, type Theme, createTheme } from '@mui/material'

const lightPalette = createTheme({
  palette: {
    mode: 'light',
    primary: {
      light: '#FA3944',
      main: '#D40511',
      dark: '#980000',
      // JIRA-#DS-53 introduced for combining old and new together...
      states: {
        focus: '#EE9BA0',
        hover: '#FBE6E7',
      },
      notContained: '#D40511',
      contrastText: '#FFFFFF',
    },
    secondary: {
      light: '#FFF0B2',
      main: '#FFCC00',
      dark: '#FFCC00',
      // JIRA-#DS-53 introduced for combining old and new together...
      states: {
        focus: '#FFEB99',
        hover: '#FFFAE5',
      },
      notContained: '#997A00',
      contrastText: '#030303',
    },
    black: {
      light: '#666666',
      main: '#333333',
      dark: '#1A1A1A',
      contrastText: '#FFFFFF',
      // JIRA-#DS-53 introduced for combining old and new together...
      states: {
        focus: '#B2B2B2',
        hover: 'rgba(0,0,0,0.08)',
      },
      notContained: '#1A1A1A',
    },
    // JIRA-#DS-346 added light for buttons
    light: {
      main: '#FFFFFF',
      light: '#FFFFFF',
      dark: '#FFFFFF',
      contrastText: '#030303',
      // JIRA-#DS-53 introduced for combining old and new together...
      states: {
        focus: '#B2B2B2',
        hover: 'rgba(255,255,255,0.5)',
      },
      notContained: '#FFFFFF',
    },
    error: {
      light: '#FF5833',
      main: '#E43A15',
      dark: '#D02601',
      // JIRA-#DS-53 introduced for combining old and new together...
      states: {
        focus: '#EEAA9B',
        hover: '#FBE6E7',
      },
      notContained: '#AA2204',
      contrastText: '#FFFFFF',
    },
    warning: {
      light: '#FF893E',
      main: '#EF6B20',
      dark: '#DB570C',
      // JIRA-#DS-53 introduced for combining old and new together...
      states: {
        focus: '#F3A05B',
        hover: '#FDF0E6',
      },
      notContained: '#5F2C01',
      contrastText: '#FFFFFF',
    },
    info: {
      light: '#1E97FD',
      main: '#0079DF',
      dark: '#0065CB',
      // JIRA-#DS-53 introduced for combining old and new together...
      states: {
        focus: '#99C9F2',
        hover: '#E5F2FC',
      },
      notContained: '#0061B2',
      contrastText: '#FFFFFF',
    },
    success: {
      light: '#1EC669',
      main: '#00A84B',
      dark: '#009437',
      // JIRA-#DS-53 introduced for combining old and new together...
      states: {
        focus: '#99DCB7',
        hover: '#E5F6ED',
      },
      notContained: '#00652D',
      contrastText: '#FFFFFF',
    },
    grey: {
      100: '#FFFFFF',
      200: '#F7F7F7',
      300: '#E7E7E7',
      400: '#B2B2B2',
      500: '#8C8C8C',
      600: '#666666',
      700: '#333333',
      800: '#000000',
    },
    common: {
      black: '#000',
      white: '#FFF',
    },
    static: {
      dark: '#030303',
      goGreen: '#006443',
      light: '#FFFEFE',
    },
    background: {
      default: '#F7F7F7',
      // Figma backdrop style uses both background color and opacity. Setting an explicit opacity value here
      // causes problems with the transition, so we'll use an approximate value here.
      backdrop: 'rgba(0, 0, 0, 0.673)',
    },
    text: {
      primary: '#1A1A1A',
      secondary: '#2F2F2F',
    },
    divider: '#E7E7E7',
    action: {
      active: '#666666',
    },

    // JIRA-#DS-53 introduced for combining old and new together...
    dark: {
      main: '#1A1A1A',
      light: '#E7E7E7',
      dark: '#030303',
      contrastText: '#FFFFFF',
      states: {
        focus: '#B2B2B2',
        hover: 'rgba(0,0,0,0.08)',
      },
      notContained: '#1A1A1A',
    },
    // JIRA-#DS-53 introduced for combining old and new together...
    pure: {
      dark: '#000000',
      light: '#FFFFFF',
      invisible: 'transparent',
    },
    // JIRA-#DS-53 introduced for combining old and new together...
    alternative: {
      main: '#E7E7E7',
      light: '#F7F7F7',
      dark: '#B2B2B2',
      contrast: '#000000',
      states: {
        focus: '#030303',
        hover: 'rgba(0,0,0,0.08)',
      },
      contrastText: '#000000',
    },
    // JIRA-#DS-53 introduced for combining old and new together...
    components: {
      avatar: {
        backgroundBlue: '#005BBB',
        border: '#B2B2B2',
      },
      backdrop: {
        fill: 'rgba(0,0,0,0.6)',
      },
      bottomNavigation: { contrast: '#FF2626' },
      buttonLink: {
        hover: '#676767',
      },
      chip: {
        icon: 'rgba(0,0,0,0.4)',
        focus: '#030303',
        border: '#B2B2B2',
        hover: 'rgba(0,0,0,0.15)',
      },
      dataGrid: {
        zebraDark: '#F7F7F7',
        hover: 'rgba(0, 0, 0, 0.10)',
      },
      divider: {
        line: '#E7E7E7',
      },
      input: {
        background: '#FFFFFF',
      },
      link: { contrast: '#FF2626' },
      navbar: {
        selected: 'rgba(212,42,5,0.10)',
        selectedHover: 'rgba(212,42,5,0.15)',
        contrast: '#FF2626',
      },
      pageHeader: {
        accept: '#00A84B',
        default: '#FFCC00',
        test: '#0076D6',
        color: {
          contrast: '#FFFFFF',
          default: '#000000',
        },
      },
      switch: {
        fill: '#FFFEFE',
        hover: 'rgba(0,0,0,0.05)',
        focus: 'rgba(0,0,0,0.08)',
        border: '#B2B2B2',
        knobDisabled: '#E7E7E7',
      },
    },
  },
}).palette

const darkPalette = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      light: '#FA3944',
      main: '#D40511',
      dark: '#D40511',
      // JIRA-#DS-53 introduced for combining old and new together...
      states: {
        focus: '#670A0E',
        hover: '#3A0B0E',
      },
      notContained: '#F24049',
      contrastText: '#FFFFFF',
    },
    secondary: {
      light: '#FFF0B2',
      main: '#FFCC00',
      dark: '#FFCC00',
      // JIRA-#DS-53 introduced for combining old and new together...
      states: {
        focus: '#6E5B12',
        hover: '#25200E',
      },
      notContained: '#FFE375',
      contrastText: '#030303',
    },
    black: {
      light: '#FFFFFF',
      main: '#FFFFFF',
      dark: '#FFFFFF',
      contrastText: '#000000',
      // JIRA-#DS-53 introduced for combining old and new together...
      states: {
        focus: '#8C8C8C',
        hover: 'rgba(255,255,255,0.08)',
      },
      notContained: '#FFFEFE',
    },
    error: {
      light: '#FF876E',
      main: '#FF6950',
      dark: '#E14B32',
      // JIRA-#DS-53 introduced for combining old and new together...
      states: {
        focus: '#671B0A',
        hover: '#23100C',
      },
      notContained: '#F15938',
      contrastText: '#000000',
    },
    warning: {
      light: '#FFB96E',
      main: '#FF9B50',
      dark: '#E17D32',
      // JIRA-#DS-53 introduced for combining old and new together...
      states: {
        focus: '#9F5101',
        hover: '#3D1F01',
      },
      notContained: '#FDE5CC',
      contrastText: '#000000',
    },
    info: {
      light: '#3AD3FF',
      main: '#1CB5FF',
      dark: '#0097E1',
      // JIRA-#DS-53 introduced for combining old and new together...
      states: {
        focus: '#083F6B',
        hover: '#0B1924',
      },
      notContained: '#54AEF8',
      contrastText: '#000000',
    },
    success: {
      light: '#3FFFA9',
      main: '#21E88B',
      dark: '#03CA6D',
      // JIRA-#DS-53 introduced for combining old and new together...
      states: {
        focus: '#08552A',
        hover: '#0A311C',
      },
      notContained: '#66DA9A',
      contrastText: '#000000',
    },
    grey: {
      100: '#262626',
      200: '#000000',
      300: '#666666',
      400: '#8C8C8C',
      500: '#B2B2B2',
      600: '#E7E7E7',
      700: '#F7F7F7',
      800: '#FFFFFF',
    },
    background: {
      default: '#000',
      backdrop: 'rgba(0, 0, 0, 0.673)',
    },
    common: {
      black: '#FFF',
      white: '#000',
    },
    static: {
      dark: '#030303',
      goGreen: '#006443',
      light: '#FFFEFE',
    },
    text: {
      primary: '#F2F2F2',
      secondary: '#E8E8E8',
    },
    divider: '#666666',
    action: {
      active: '#E7E7E7',
    },

    // JIRA-#DS-53 introduced for combining old and new together...
    dark: {
      main: '#F2F2F2',
      light: '#424242',
      dark: '#FFFEFE',
      contrastText: '#000000',
      states: {
        focus: '#8C8C8C',
        hover: 'rgba(255,255,255,0.08)',
      },
      notContained: '#FFFEFE',
    },
    // JIRA-#DS-346 introduced for combining old and new together...
    light: {
      main: '#000000',
      light: '#000000',
      dark: '#000000',
      contrastText: '#FFFEFE',
      states: {
        focus: '#8C8C8C',
        hover: 'rgba(0,0,0,0.5)',
      },
      notContained: '#000000',
    },
    // JIRA-#DS-53 introduced for combining old and new together...
    pure: {
      dark: '#FFFFFF',
      light: '#000000',
      invisible: 'transparent',
    },
    // JIRA-#DS-53 introduced for combining old and new together...
    alternative: {
      main: '#424242',
      dark: '#8C8C8C',
      light: '#1C1C1E',
      contrast: '#FFFFFF',
      states: {
        focus: '#FFFEFE',
        hover: 'rgba(255,255,255,0.08)',
      },
      contrastText: '#FFFFFF',
    },
    // JIRA-#DS-53 introduced for combining old and new together...
    components: {
      avatar: {
        backgroundBlue: '#0073EE',
        border: '#8C8C8C',
      },
      backdrop: {
        fill: 'rgba(0,0,0,0.6)',
      },
      bottomNavigation: { contrast: '#D40511' },
      buttonLink: {
        hover: '#D0D0D0',
      },
      chip: {
        icon: 'rgba(255,255,255,0.4)',
        focus: '#FFFEFE',
        border: '#8C8C8C',
        hover: 'rgba(255,255,255,0.15)',
      },
      dataGrid: {
        zebraDark: '#1C1C1E',
        hover: 'rgba(255, 255, 255, 0.10)',
      },
      divider: {
        line: '#424242',
      },
      input: {
        background: '#000000',
      },
      link: { contrast: '#D73431' },
      navbar: {
        selected: 'rgba(237,6,17,0.10)',
        selectedHover: 'rgba(237,6,17,0.15)',
        contrast: '#FFFFFF',
      },
      pageHeader: {
        accept: '#0A311C',
        default: '#FFD119',
        test: '#0A263C',
        color: {
          contrast: '#FFFFFF',
          default: '#FFFFFF',
        },
      },
      switch: {
        fill: '#030303',
        hover: 'rgba(255,255,255,0.05)',
        focus: 'rgba(255,255,255,0.08)',
        border: '#8C8C8C',
        knobDisabled: '#8C8C8C',
      },
    },
  },
}).palette

export const createPalette = (mode: PaletteMode): Theme['palette'] => (mode === 'light' ? lightPalette : darkPalette)
